// §2 — Closing Details + Borrower Vesting. Ported from Vesting.jsx
// (ClosingSection + VestingWorkflow wizard + AttorneyTrustReview + LlcEntityInfo).
// Data plumbing is remapped to the production contract: manual closing fields →
// ctx.patchClosing (snake_case); the wizard commits vesting/workflow/confirmed in
// one ctx.patchVesting on Save. Every doc-dating / vesting rule comes from
// doc-order-core (deriveDocDating, derivedTypes, buildFinalVesting, …) — nothing
// re-implemented. Branches: processor-editable form · closing review (with the
// enc-vesting-differs ATTEST control → vesting-attest, A1.5) · read-only summary.
// Still omitted vs the prototype: the Trust Review Fee useEffect (the server
// injects/removes that fee when closingInTrust flips on the vesting save).
// The wizard's title-only question is tri-state (null = blank start, bug 3.1).
// Encompass Field 1867 is read-only here and remains the vesting source of
// truth; the processor submission is retained solely for comparison.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Chip, EncFlag, Icon, StatusPill, SumField } from '../atoms';
import { useDocOrderCtx, type ClosingPatch } from '../context';
import { fmtDate, fmtStamp } from '../format';
import {
  DO_LLC_ORG_TYPES,
  DO_US_STATES,
  TRUST_ORG_TYPES,
  buildFinalVesting,
  deriveDocDating,
  derivedTypes,
  entityStateDiffersFromProperty,
  lockExpiredAtClosing,
  monthIndex,
  resolveClosingDates,
  vestingSaveReady,
  type VestingWorkflowState
} from '../core';
import type { DocOrderRow } from '@/services/docOrderService';

// ---- Display helpers (presentation only — no business rules) ---------------

/** Absolute month index → localized label (port of data.js DO_monthName). */
function monthName(idx: number | null, opts?: Intl.DateTimeFormatOptions): string {
  if ((idx === null || idx === undefined)) {
return '';
}
  const y = Math.floor(idx / 12);
  const m = idx % 12;
  return new Date(y, m, 1).toLocaleDateString('en-US', opts ?? { month: 'long', year: 'numeric' });
}

/** First-payment value ('YYYY-MM' or 'YYYY-MM-DD') → "Month Year". */
/** Prototype CloserSelectField port ("12 (2).png"): read-only value + pencil
 *  that swaps to an inline select; commit on change/blur. */
function CloserSelectField({
  label,
  value,
  options,
  editing,
  onEdit,
  onDone,
  onChange
}: {
  label: string;
  value: string | null;
  options: string[];
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onChange: (v: string) => void;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {editing ? (
        <select
          className="inp closer-select"
          autoFocus
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
            onDone();
          }}
          onBlur={onDone}
        >
          {!value && (
            <option value="" disabled>
              Select…
            </option>
          )}
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <div className="closer-ro">
          <span>{value || <span className="vsum-empty">Not set</span>}</span>
          <button type="button" className="closer-pencil" title={`Edit ${label}`} onClick={onEdit}>
            <Icon name="edit" size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function fpLabelFrom(value: string): string {
  return monthName(monthIndex(value));
}

/** Presentation check (no core equivalent, cf. Insurance.tsx effectiveAfterFunding):
 *  funding must be on or after signing. */
function isFundingBeforeSigning(signing: string | null, closing: string | null): boolean {
  if (!signing || !closing) {
return false;
}
  return new Date(closing) < new Date(signing);
}

/** EIN mask: keep up to 9 digits, format XX-XXXXXXX while typing. */
function formatEin(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
}

// ---- Wizard draft ----------------------------------------------------------
// The slide-over edits a LOCAL draft (seeded once from the order) so intermediate
// keystrokes don't autosave-storm; the final Save commits the whole thing.
type WizardDraft = VestingWorkflowState & { vesting: string };

function seedDraft(order: DocOrderRow): WizardDraft {
  const wf: Partial<VestingWorkflowState> = order.vesting_workflow ?? {};
  return {
    // null = unanswered — the wizard loads BLANK until the processor picks
    // Yes or No (Jennifer bug 3.1). Prior saves carry an explicit boolean.
    titleOnlyBorrower: wf.titleOnlyBorrower ?? null,
    titleOnlyRole: wf.titleOnlyRole ?? null,
    titleOnlyName: wf.titleOnlyName ?? '',
    titleOnlyVestingType: wf.titleOnlyVestingType ?? null,
    entityNeither: wf.entityNeither ?? false,
    closingInTrust: wf.closingInTrust ?? false,
    closingInLlc: wf.closingInLlc ?? false,
    vestingType: wf.vestingType ?? '',
    finalVesting: wf.finalVesting ?? '',
    printTrustOnAddendum: wf.printTrustOnAddendum ?? false,
    trustName: wf.trustName ?? '',
    trustOrgState: wf.trustOrgState ?? '',
    trustOrgType: wf.trustOrgType ?? '',
    trustTaxId: wf.trustTaxId ?? '',
    trustDate: wf.trustDate ?? '',
    trustAmendedDate: wf.trustAmendedDate ?? '',
    attorneyTrustReviewNotRequired: wf.attorneyTrustReviewNotRequired ?? false,
    llcEntityName: wf.llcEntityName ?? null,
    llcEntityState: wf.llcEntityState ?? null,
    llcEntityOrgType: wf.llcEntityOrgType ?? null,
    llcEntityEIN: wf.llcEntityEIN ?? null,
    vesting: order.vesting ?? ''
  };
}

// ---- Small shared sub-components -------------------------------------------

function Choice({
  active,
  onClick,
  children,
  sub,
  wide
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  sub?: string;
  wide?: boolean;
}) {
  return (
    <button type="button" className={`vw-choice ${active ? 'on' : ''} ${wide ? 'wide' : ''}`.trim()} onClick={onClick}>
      <span className="vw-choice-mark">{active && <Icon name="check" size={11} strokeWidth={3} />}</span>
      <span className="vw-choice-body">
        <b>{children}</b>
        {sub && <span className="vw-choice-sub">{sub}</span>}
      </span>
    </button>
  );
}

interface SegOpt {
  value: string;
  label: string;
  tip?: string;
}

// Segmented control with per-option disable/tip (the SegField atom can't express
// those), so ClosingSection keeps its own — same .seg-field markup as the atom.
function ClosingSeg({
  value,
  options,
  disabledOptions,
  editable,
  onPick
}: {
  value: string | null;
  options: SegOpt[];
  disabledOptions: string[];
  editable: boolean;
  onPick: (v: string) => void;
}) {
  return (
    <div className={`seg-field ${editable && !value ? 'unset' : ''}`.trim()}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={value === opt.value ? 'active' : ''}
          disabled={!editable || disabledOptions.includes(opt.value)}
          title={opt.tip}
          onClick={() => onPick(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Inline status row. State is derived from the single contract field
// attorneyTrustReviewNotRequired (the eFolder auto-detect / reviewer-name / audit
// stamp from the prototype need docs+fees+audit columns absent from this
// component's inputs — omitted). Trust Review FEE stays server-driven.
function AttorneyTrustReview({
  notRequired,
  editable,
  onToggle
}: {
  notRequired: boolean;
  editable: boolean;
  onToggle?: () => void;
}) {
  if (notRequired) {
    return (
      <div className="atr-row atr-neutral" role="status">
        <Icon name="alertCircle" size={13} className="atr-row-ico" />
        <span className="atr-row-label">Attorney Trust Review completed — no separate fee charged</span>
        {editable && onToggle && (
          <button type="button" className="atr-row-link" onClick={onToggle}>
            Undo
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="atr-row atr-warn" role="status">
      <Icon name="alertTriangle" size={13} className="atr-row-ico" />
      <span className="atr-row-label">A trust closing requires an attorney&rsquo;s trust review on file.</span>
    </div>
  );
}

// LLC Entity Information — surfaces when "Closing in LLC?" is chosen. Fields are
// provenance-tagged to their Encompass field IDs (1859–1862); read-only "synced
// from Encompass" only — no write-back claim (P3).
function LlcEntityInfo({
  draft,
  set,
  editable,
  propertyState
}: {
  draft: WizardDraft;
  set: (patch: Partial<WizardDraft>) => void;
  editable: boolean;
  propertyState: string | null;
}) {
  const missing = !draft.llcEntityName || !draft.llcEntityState || !draft.llcEntityOrgType || !draft.llcEntityEIN;
  const einValid = !draft.llcEntityEIN || /^\d{2}-\d{7}$/.test(draft.llcEntityEIN);
  const stateMismatch = entityStateDiffersFromProperty(draft.llcEntityState, propertyState);

  return (
    <div className={`llc-entity-block ${missing ? 'has-missing' : ''}`.trim()}>
      <div className="llc-entity-head">
        <div className="llc-entity-title">
          <Icon name="building" size={11} />LLC Entity Information
        </div>
        <EncFlag label="Synced from Encompass" />
      </div>
      <div className="llc-entity-grid">
        <div className="llc-field">
          <span className="llc-lbl">
            Name of Entity
            <span className="enc-sync-flag" title="Field 1859 — Borrower Vesting Borr 1 Corp/Trust Name">
              <Icon name="link" size={9} />
            </span>
          </span>
          <input
            className="inp"
            type="text"
            value={draft.llcEntityName ?? ''}
            disabled={!editable}
            placeholder="Park Wilshire 1605, LLC …"
            onChange={(e) => set({ llcEntityName: e.target.value })}
          />
        </div>
        <div className="llc-field">
          <span className="llc-lbl">
            State
            {stateMismatch && <Icon name="alertTriangle" size={10} className="llc-warn-ico" />}
            <span className="enc-sync-flag" title="Field 1860 — Borrower Vesting Borr 1 Org State">
              <Icon name="link" size={9} />
            </span>
          </span>
          <select
            className="inp"
            value={draft.llcEntityState ?? ''}
            disabled={!editable}
            onChange={(e) => set({ llcEntityState: e.target.value })}
          >
            <option value="" disabled>
              Select…
            </option>
            {DO_US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="llc-field">
          <span className="llc-lbl">
            Org Type
            <span className="enc-sync-flag" title="Field 1861 — Borrower Vesting Borr 1 Org Type">
              <Icon name="link" size={9} />
            </span>
          </span>
          <select
            className="inp"
            value={draft.llcEntityOrgType ?? ''}
            disabled={!editable}
            onChange={(e) => set({ llcEntityOrgType: e.target.value })}
          >
            <option value="" disabled>
              Select…
            </option>
            {DO_LLC_ORG_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="llc-field">
          <span className="llc-lbl">
            EIN / Tax ID
            {!einValid && <Icon name="alertTriangle" size={10} className="llc-warn-ico" />}
            <span className="enc-sync-flag" title="Field 1862 — Borrower Vesting Borr 1 Org Tax ID">
              <Icon name="link" size={9} />
            </span>
          </span>
          <input
            className={`inp mono ${!einValid ? 'inp-warn' : ''}`.trim()}
            type="text"
            value={draft.llcEntityEIN ?? ''}
            disabled={!editable}
            placeholder="99-1527884"
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => set({ llcEntityEIN: formatEin(e.target.value) })}
          />
        </div>
      </div>
      {missing && editable && (
        <div className="llc-entity-note">
          <Icon name="alertTriangle" size={10} />Entity information is incomplete — confirm in Encompass before submitting.
        </div>
      )}
      {!missing && stateMismatch && editable && (
        <div className="llc-entity-note">
          <Icon name="alertTriangle" size={10} />Entity State <b>{draft.llcEntityState}</b> differs from the subject property state <b>{propertyState}</b>.
        </div>
      )}
    </div>
  );
}

// ---- Vesting slide-over wizard ---------------------------------------------
// Mounts only while open. Holds the local draft and runs the 3-step flow.
// R4 #20 — every advance PERSISTS: Next queues the partial draft (with
// vesting_confirmed:false — partial is by definition unconfirmed) and AWAITS
// flush(); a failed save stays on the step with the error visible instead of
// silently advancing past unsaved work. The final Save awaits success BEFORE
// closing, and is the only place vesting_confirmed flips true. Rendered
// INLINE — no portal — so it stays interactive inside the Radix
// LoanDetailsSheet's modal pointer-events lock (bugs 3/4).
function VestingSlideOver({ order, onClose }: { order: DocOrderRow; onClose: () => void }) {
  const { editable, patchVesting, flush, loanFacts } = useDocOrderCtx();
  const [draft, setDraft] = useState<WizardDraft>(() => seedDraft(order));
  const [step, setStep] = useState(0);
  const [advancing, setAdvancing] = useState(false);
  const [stepSaveFailed, setStepSaveFailed] = useState(false);
  const set = (patch: Partial<WizardDraft>) => setDraft((d) => ({ ...d, ...patch }));

  // Queue the whole draft as the vesting section patch, then drain it NOW.
  // TRUE = persisted; FALSE = stalled/rejected (dirty state intact — the hook
  // toasted the reason; we surface an inline line and stay put).
  const persistDraft = async (confirmed: boolean): Promise<boolean> => {
    const { vesting, ...workflow } = draft;
    patchVesting({ vesting, vesting_workflow: workflow, vesting_confirmed: confirmed });
    return await flush();
  };

  const sheetRef = useRef<HTMLElement>(null);
  const vestingRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the Final Vesting textarea to fit the whole clause.
  useEffect(() => {
    const el = vestingRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [draft.vesting, step]);

  // Dialog a11y: focus in, trap Tab, Escape closes, return focus on unmount.
  useEffect(() => {
    const prevActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sheet = sheetRef.current;
    const focusableSel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    sheet?.querySelector<HTMLElement>(focusableSel)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !sheet) {
return;
}
      const items = Array.from(sheet.querySelectorAll<HTMLElement>(focusableSel)).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
return;
}
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      prevActive?.focus?.();
    };
  }, [onClose]);

  const entity: 'trust' | 'llc' | 'none' | null = draft.closingInTrust
    ? 'trust'
    : draft.closingInLlc
      ? 'llc'
      : draft.entityNeither
        ? 'none'
        : null;
  const hasTitleOnly = !!draft.titleOnlyBorrower;

  const pickEntity = (val: 'trust' | 'llc' | 'none') => {
    if (val === 'trust') {
      const d = derivedTypes('trust');
      set({ closingInTrust: true, closingInLlc: false, entityNeither: false, vestingType: d.primary, titleOnlyVestingType: hasTitleOnly ? d.titleOnly : null });
    } else if (val === 'llc') {
      const d = derivedTypes('llc');
      set({ closingInLlc: true, closingInTrust: false, entityNeither: false, vestingType: d.primary, titleOnlyVestingType: hasTitleOnly ? d.titleOnly : null });
    } else {
      set({ closingInTrust: false, closingInLlc: false, entityNeither: true, vestingType: 'Individual', titleOnlyVestingType: hasTitleOnly ? 'Non Title Spouse' : null });
    }
  };
  const setTitleOnly = (yes: boolean) => {
    if (yes) {
      set({
        titleOnlyBorrower: true,
        titleOnlyVestingType: entity === 'trust' ? 'Title Only Settlor Trustee' : entity === 'llc' ? 'Officer' : 'Non Title Spouse'
      });
    } else {
      set({ titleOnlyBorrower: false, titleOnlyName: '', titleOnlyRole: null, titleOnlyVestingType: null });
    }
  };

  const steps = ['Title-only', 'Closing entity', 'Review'];
  // The question must be ANSWERED (null = blank start, bug 3.1): No advances
  // directly; Yes additionally needs the role + name.
  const titleOnlyDone =
    draft.titleOnlyBorrower === false ||
    (draft.titleOnlyBorrower === true && !!(draft.titleOnlyRole && draft.titleOnlyName));
  const canNext = [titleOnlyDone, entity !== null, true][step];
  // Save is completion-driven (no manual "Confirmed" toggle): a title-only
  // answer, an entity decision, and a non-empty final clause. Flips
  // vesting_confirmed=true on commit.
  const saveReady = vestingSaveReady(draft, draft.vesting);
  // LLC: all four entity fields present → the one-click "Build Vesting" action.
  const llcComplete =
    entity === 'llc' && !!(draft.llcEntityName && draft.llcEntityState && draft.llcEntityOrgType && draft.llcEntityEIN);

  const handleNext = async () => {
    if (advancing) {
      return;
    }
    setAdvancing(true);
    setStepSaveFailed(false);
    try {
      if (await persistDraft(false)) {
        setStep((s) => s + 1);
      } else {
        setStepSaveFailed(true); // stay on the step — the edit is still queued
      }
    } finally {
      setAdvancing(false);
    }
  };
  const handleSave = async () => {
    if (advancing) {
      return;
    }
    setAdvancing(true);
    setStepSaveFailed(false);
    try {
      if (await persistDraft(true)) {
        onClose(); // close ONLY on a confirmed persisted save
      } else {
        setStepSaveFailed(true);
      }
    } finally {
      setAdvancing(false);
    }
  };

  // INLINE render (no portal): the Radix LoanDetailsSheet's modal lock makes any
  // document.body portal pointer-inert (Jennifer bugs 3/4) — staying inside the
  // dialog's DOM keeps clicks, focus, and outside-press detection working.
  return (
    <div className="recon-overlay" data-doc-order>
      <div className="recon-scrim" onClick={onClose} />
      <aside className="recon-sheet" role="dialog" aria-label="Vesting Details" ref={sheetRef}>
        <header className="recon-hd">
          <div>
            <div className="recon-eyebrow">
              <Icon name="scale" size={12} />Vesting Details
            </div>
            <div className="recon-title">Borrower Vesting</div>
          </div>
          <button type="button" className="recon-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={15} />
          </button>
        </header>

        <div className="vw-steprail">
          {steps.map((s, i) => (
            <div key={s} className={`vw-step ${i === step ? 'cur' : ''} ${i < step ? 'done' : ''}`.trim()}>
              <span className="vw-step-dot">{i < step ? <Icon name="check" size={11} strokeWidth={3} /> : i + 1}</span>
              <span className="vw-step-lbl">{s}</span>
            </div>
          ))}
        </div>

        <div className="vw-stepbody">
          {step === 0 && (
            <>
              <div className="vw-q">Is there a title-only borrower?</div>
              <div className="vw-choices row">
                <Choice active={draft.titleOnlyBorrower === true} onClick={() => setTitleOnly(true)}>
                  Yes
                </Choice>
                <Choice active={draft.titleOnlyBorrower === false} onClick={() => setTitleOnly(false)}>
                  No
                </Choice>
              </div>
              {hasTitleOnly && (
                <div className="vw-sub">
                  <div className="vw-q sm">Title only, or non-title spouse?</div>
                  <div className="vw-choices row">
                    <Choice
                      active={draft.titleOnlyRole === 'Title Only'}
                      onClick={() =>
                        set({
                          titleOnlyRole: 'Title Only',
                          titleOnlyVestingType: entity === 'trust' ? 'Title Only Settlor Trustee' : entity === 'llc' ? 'Officer' : 'Title only'
                        })
                      }
                    >
                      Title Only
                    </Choice>
                    <Choice
                      active={draft.titleOnlyRole === 'Non-Title Spouse'}
                      onClick={() => set({ titleOnlyRole: 'Non-Title Spouse', titleOnlyVestingType: 'Non Title Spouse' })}
                    >
                      Non-Title Spouse
                    </Choice>
                  </div>
                  <div className="field" style={{ marginTop: 10 }}>
                    <span className="field-label" style={{ fontSize: 11 }}>
                      Name as it appears on docs <span className="req">*</span>
                    </span>
                    <input
                      className="inp"
                      type="text"
                      value={draft.titleOnlyName}
                      placeholder="e.g. Robert James Willis"
                      disabled={!editable}
                      onChange={(e) => set({ titleOnlyName: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <div className="vw-q">Closing in a trust or an LLC?</div>
              <div className="vw-choices">
                <Choice active={entity === 'trust'} onClick={() => pickEntity('trust')}>
                  Closing in Trust
                </Choice>
                <Choice active={entity === 'llc'} onClick={() => pickEntity('llc')}>
                  Closing in LLC
                </Choice>
                <Choice active={entity === 'none'} onClick={() => pickEntity('none')}>
                  Neither
                </Choice>
              </div>

              {draft.closingInTrust && (
                <div className="vd-block">
                  <div className="vd-block-h">
                    <Icon name="scale" size={12} />Trust Information
                  </div>
                  <div className="vd-grid">
                    <div className="vd-field vd-span2">
                      <span className="vd-lbl">Trust Name</span>
                      <input
                        className="inp"
                        type="text"
                        value={draft.trustName}
                        placeholder="e.g. The Willis Family Trust"
                        disabled={!editable}
                        onChange={(e) => set({ trustName: e.target.value })}
                      />
                    </div>
                    <div className="vd-field">
                      <span className="vd-lbl">Org. State</span>
                      <select className="inp" value={draft.trustOrgState} disabled={!editable} onChange={(e) => set({ trustOrgState: e.target.value })}>
                        <option value="" disabled>
                          Select…
                        </option>
                        {DO_US_STATES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="vd-field">
                      <span className="vd-lbl">Org. Type</span>
                      <select className="inp" value={draft.trustOrgType} disabled={!editable} onChange={(e) => set({ trustOrgType: e.target.value })}>
                        <option value="" disabled>
                          Select…
                        </option>
                        {TRUST_ORG_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="vd-field">
                      <span className="vd-lbl">Trust Date / Year</span>
                      <input
                        className="inp"
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={draft.trustDate}
                        placeholder="MM/DD/YYYY"
                        disabled={!editable}
                        onChange={(e) => {
                          const d = e.target.value.replace(/\D/g, '').slice(0, 8);
                          const out =
                            d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
                          set({ trustDate: out });
                        }}
                      />
                    </div>
                  </div>
                  <AttorneyTrustReview
                    notRequired={draft.attorneyTrustReviewNotRequired}
                    editable={editable}
                    onToggle={() => set({ attorneyTrustReviewNotRequired: !draft.attorneyTrustReviewNotRequired })}
                  />
                </div>
              )}
              {draft.closingInLlc && (
                <div className="vd-block">
                  <LlcEntityInfo draft={draft} set={set} editable={editable} propertyState={loanFacts.propertyState} />
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="vw-q">Review &amp; final vesting</div>
              <div className="vd-block-h" style={{ justifyContent: 'space-between', marginTop: 4 }}>
                <span className="vd-block-h-l">
                  <Icon name="link" size={11} style={{ color: 'var(--accent)' }} />Final Vesting To Read
                </span>
                {editable && llcComplete && (
                  <button type="button" className="vw-btn ghost sm" onClick={() => set({ vesting: buildFinalVesting(draft, draft.vesting) })}>
                    <Icon name="zap" size={12} />Build Vesting
                  </button>
                )}
              </div>
              <textarea
                ref={vestingRef}
                className="recon-note vd-final"
                rows={2}
                value={draft.vesting}
                placeholder="e.g. Wilhermina Willis, an unmarried person, as sole and separate property"
                disabled={!editable}
                onChange={(e) => set({ vesting: e.target.value })}
              />
            </>
          )}
        </div>

        <div className="vw-foot">
          <button type="button" className="vw-btn ghost" disabled={step === 0 || advancing} onClick={() => setStep((s) => s - 1)}>
            <Icon name="arrowLeft" size={13} />Back
          </button>
          {stepSaveFailed && (
            <span className="vw-save-fail" role="alert">
              <Icon name="alertTriangle" size={12} />
              Couldn&rsquo;t save this step — retry.
            </span>
          )}
          {step < 2 ? (
            <button type="button" className="vw-btn primary" disabled={!canNext || advancing} onClick={() => void handleNext()}>
              {advancing ? 'Saving…' : 'Next'}<Icon name="arrowRight" size={13} />
            </button>
          ) : (
            <button type="button" className="vw-btn primary" disabled={!saveReady || advancing} onClick={() => void handleSave()}>
              <Icon name="check" size={14} />{advancing ? 'Saving…' : 'Save vesting'}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

// ---- Section 2 -------------------------------------------------------------

interface ClosingVestingProps {
  order: DocOrderRow;
  stepNo: number;
}

export function ClosingVestingSection({ order, stepNo }: ClosingVestingProps) {
  const { editable, role, loanFacts, patchClosing, vestingAttest, setFirstPaymentOverride, addToast, busy } = useDocOrderCtx();
  const [vestingOpen, setVestingOpen] = useState(false);
  // Closer-branch local state (R5): per-field pencil + the FP override editor.
  const [closerEditField, setCloserEditField] = useState<'poa' | 'sign' | null>(null);
  const [fpEditorOpen, setFpEditorOpen] = useState(false);
  const [fpMonth, setFpMonth] = useState('');
  const [fpReason, setFpReason] = useState('');

  const wf: Partial<VestingWorkflowState> = order.vesting_workflow ?? {};
  const entityClosing = !!(wf.closingInTrust || wf.closingInLlc);

  const sIdx = monthIndex(order.signing_date);
  const cIdx = monthIndex(order.closing_date);
  const dd = deriveDocDating(order.signing_date, order.closing_date, order.doc_dating);
  const { crossesMonth, fundDeepIntoMonth } = dd;
  // Funding > cutoff days into the next month makes 'fund-into' invalid, so
  // 'next-month' is the effective (and persisted) choice.
  const effectiveDating: 'fund-into' | 'next-month' | null = fundDeepIntoMonth ? 'next-month' : order.doc_dating;
  const displayDd = fundDeepIntoMonth ? deriveDocDating(order.signing_date, order.closing_date, 'next-month') : dd;

  // Every date/doc-dating patch PERSISTS the derived first payment alongside
  // it (or clears it while cross-month dating is unchosen). The field renders
  // read-only, but the server's six-field sign gate reads the STORED value —
  // without this, sign-off is unreachable (round-12 finding 1).
  const patchClosingDates = useCallback(
    (patch: ClosingPatch) => {
      // Same core resolver the server uses (force rule + derivation) — the
      // client value is optimistic display only; the server re-derives and
      // overwrites regardless (round-13: the invariant is server-owned).
      const resolved = resolveClosingDates(
        'signing_date' in patch ? (patch.signing_date as string | null) : order.signing_date,
        'closing_date' in patch ? (patch.closing_date as string | null) : order.closing_date,
        'doc_dating' in patch ? (patch.doc_dating as 'fund-into' | 'next-month' | null) : order.doc_dating
      );
      patchClosing({ ...patch, doc_dating: resolved.docDating, first_payment_date: resolved.firstPayment || null });
    },
    [order.signing_date, order.closing_date, order.doc_dating, patchClosing]
  );

  const lockExpired = lockExpiredAtClosing(loanFacts.lockExpiration, order.closing_date);
  const fundingBeforeSigning = isFundingBeforeSigning(order.signing_date, order.closing_date);

  // Persist the forced 'next-month' once (bounded: the guard falls false after
  // the optimistic order update).
  useEffect(() => {
    if (!editable) {
return;
}
    if (fundDeepIntoMonth && order.doc_dating !== 'next-month') {
patchClosingDates({ doc_dating: 'next-month' });
}
  }, [editable, fundDeepIntoMonth, order.doc_dating, patchClosingDates]);

  // Trust/LLC closings forbid POA and Hybrid eClose (trustee / managing member
  // must sign wet-ink). Auto-correct stale picks and fill the only valid option.
  useEffect(() => {
    if (!editable || !entityClosing) {
return;
}
    const patch: ClosingPatch = {};
    if (!order.power_of_attorney || order.power_of_attorney === 'Yes') {
patch.power_of_attorney = 'No';
}
    if (!order.sign_type || order.sign_type === 'Hybrid eClose') {
patch.sign_type = 'Wet Sign';
}
    if (Object.keys(patch).length) {
patchClosing(patch);
}
  }, [editable, entityClosing, order.power_of_attorney, order.sign_type, patchClosing]);

  // R4 #17 (one rule, mirrors the server canonicalization): Power of Attorney
  // requires Wet Sign on ANY closing — auto-correct a stale Hybrid pick the
  // moment POA flips to Yes (save-draft canonicalizes; sign/submit reject).
  const poaYes = order.power_of_attorney === 'Yes';
  useEffect(() => {
    if (!editable || !poaYes) {
      return;
    }
    if (order.sign_type === 'Hybrid eClose') {
      patchClosing({ sign_type: 'Wet Sign' });
    }
  }, [editable, poaYes, order.sign_type, patchClosing]);

  // ---- CLOSER view (R5 #11/#12 — target "12 (2).png"): faithful port of the
  // prototype's closing-role ClosingSection. Headline dates (read-only),
  // processor-vs-Encompass vesting comparison + attestation, per-field pencil
  // edits for POA/Signing (patchClosing — submitted-legal since R4-A), and the
  // CONTROLLED First-Payment override (Anthony's contract — a raw input would
  // be silently re-derived away). DEVIATION from the target screenshot,
  // Anthony-approved: no inline Encompass-vesting editor — correct Field 1867
  // in Encompass, hit Refresh, then confirm (the warn copy says exactly that).
  if (role === 'closing' && editable) {
    const encVesting = ((order.encompass_vesting as string | null) ?? '').trim();
    const vestingDiffers = encVesting !== '' && encVesting !== (order.vesting ?? '');
    const confirmed = !!order.encompass_vesting_confirmed;
    const override = order.first_payment_override;
    const effectiveFp = override?.month ?? order.first_payment_date;
    const fpLabel = effectiveFp ? fpLabelFrom(effectiveFp) : '';
    return (
      <section className="ws-section" id="sec-closing">
        <div className="card card-pad">
          <div className="ws-section-head">
            <h3 className="ws-section-title">
              <span className="ws-step-no">{stepNo}</span>Closing Details
            </h3>
          </div>

          <div className="reqdates-header centered">
            <span>Docs requested by <b>{order.signing_date ? fmtDate(order.signing_date) : 'TBD'}</b></span>
            <span className="reqdates-sep">·</span>
            <span>Funding on <b>{order.closing_date ? fmtDate(order.closing_date) : 'TBD'}</b></span>
          </div>

          <div className="vesting-summary-block">
            <span className="field-label">
              Vesting submitted by Processor <span className="req">*</span>
            </span>
            <div className="enc-vest-row">
              <div className="enc-vest-val">{order.vesting || <span className="vsum-empty">Not set</span>}</div>
            </div>
            {(wf.titleOnlyBorrower || wf.closingInTrust || wf.closingInLlc) && (
              <div className="vsum-flags">
                {wf.closingInTrust && (
                  <span className="vsum-pill"><Icon name="check" size={10} strokeWidth={3} />Closing in Trust</span>
                )}
                {wf.closingInLlc && (
                  <span className="vsum-pill"><Icon name="check" size={10} strokeWidth={3} />Closing in LLC</span>
                )}
                {wf.titleOnlyBorrower && (
                  <span className="vsum-pill">
                    <Icon name="user" size={10} />
                    {wf.closingInTrust ? 'Title Only Trustee' : 'Title Only Borrower'}
                    {wf.titleOnlyName ? ` · ${wf.titleOnlyName}` : ''}
                  </span>
                )}
              </div>
            )}

            <div className="enc-vest-block">
              <span className="field-label">Current vesting per Encompass</span>
              <div className="enc-vest-row">
                <div className="enc-vest-val">{encVesting || <span className="vsum-empty">Empty in Encompass</span>}</div>
              </div>
              {encVesting === '' ? (
                <span className="enc-vest-note">
                  <Icon name="link" size={11} />
                  No vesting on file in Encompass. Doc Order will not update this field; correct it in Encompass and refresh if needed.
                </span>
              ) : vestingDiffers ? (
                <div className={`enc-vest-attest ${confirmed ? 'is-confirmed' : ''}`.trim()}>
                  <span className={`enc-vest-note ${confirmed ? '' : 'diff'}`.trim()}>
                    <Icon name={confirmed ? 'checkCircle' : 'alertTriangle'} size={11} />
                    Differs from the vesting submitted by the Processor. Verify for accuracy and confirm once Encompass reflects the correct data.
                  </span>
                  <label className={`enc-vest-confirm ${confirmed ? 'done' : ''}`.trim()}>
                    {!confirmed && <span className="req-star" title="Required before sending to the LO">*</span>}
                    <input
                      type="checkbox"
                      checked={confirmed}
                      disabled={busy || confirmed}
                      onChange={() => void vestingAttest()}
                    />
                    <span>
                      {confirmed
                        ? `Confirmed — current vesting per Encompass is accurate.${order.encompass_vesting_confirmed_at ? ` · ${fmtStamp(order.encompass_vesting_confirmed_at)}` : ''}`
                        : 'I confirm the current vesting per Encompass is accurate.'}
                    </span>
                  </label>
                </div>
              ) : (
                <span className="enc-vest-note ok">
                  <Icon name="checkCircle" size={11} />
                  Matches the submitted vesting.
                </span>
              )}
            </div>
          </div>

          <div className="field-grid closer-grid">
            <div className="field">
              <span className="field-label">First Payment Date</span>
              {fpEditorOpen ? (
                <div className="fp-override-editor">
                  <input
                    type="month"
                    className="inp closer-select"
                    autoFocus
                    value={fpMonth}
                    onChange={(e) => setFpMonth(e.target.value)}
                  />
                  <input
                    className="inp"
                    type="text"
                    placeholder="Reason for the override (required)"
                    value={fpReason}
                    onChange={(e) => setFpReason(e.target.value)}
                  />
                  <div className="enc-vest-actions">
                    <button
                      type="button"
                      className="enc-vest-btn primary"
                      disabled={busy || !fpMonth || !fpReason.trim()}
                      onClick={async () => {
                        const res = await setFirstPaymentOverride({ month: fpMonth, reason: fpReason.trim() });
                        if (!res.ok) {
                          addToast(res.error || 'Could not save the override', 'alertTriangle');
                          return;
                        }
                        setFpEditorOpen(false);
                      }}
                    >
                      <Icon name="check" size={12} />Save override
                    </button>
                    {override && (
                      <button
                        type="button"
                        className="enc-vest-btn"
                        disabled={busy}
                        onClick={async () => {
                          const res = await setFirstPaymentOverride({ clear: true });
                          if (!res.ok) {
                            addToast(res.error || 'Could not clear the override', 'alertTriangle');
                            return;
                          }
                          setFpEditorOpen(false);
                        }}
                      >
                        <Icon name="rotateCcw" size={12} />Clear override
                      </button>
                    )}
                    <button type="button" className="enc-vest-btn" onClick={() => setFpEditorOpen(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="closer-ro">
                  <span>{fpLabel || <span className="vsum-empty">Not set</span>}</span>
                  <button
                    type="button"
                    className="closer-pencil"
                    title="Override the First Payment Date (tracked, with a reason — the derived date stays authoritative for signing)"
                    onClick={() => {
                      setFpMonth(override?.month ?? order.first_payment_date ?? '');
                      setFpReason(override?.reason ?? '');
                      setFpEditorOpen(true);
                    }}
                  >
                    <Icon name="edit" size={13} />
                  </button>
                </div>
              )}
              {override && !fpEditorOpen && (
                <span className="rail-note" style={{ marginTop: 4 }}>
                  Overridden by {override.actor_name ?? 'Closing'} — derived {override.derived_month ? fpLabelFrom(override.derived_month) : '—'} · &ldquo;{override.reason}&rdquo;
                </span>
              )}
            </div>
            <CloserSelectField
              label="Power of Attorney?"
              value={order.power_of_attorney}
              options={['No', 'Yes']}
              editing={closerEditField === 'poa'}
              onEdit={() => setCloserEditField('poa')}
              onDone={() => setCloserEditField(null)}
              onChange={(v) => patchClosing({ power_of_attorney: v })}
            />
            <CloserSelectField
              label="Signing Method"
              value={order.sign_type}
              options={order.power_of_attorney === 'Yes' ? ['Wet Sign'] : ['Hybrid eClose', 'Wet Sign']}
              editing={closerEditField === 'sign'}
              onEdit={() => setCloserEditField('sign')}
              onDone={() => setCloserEditField(null)}
              onChange={(v) => patchClosing({ sign_type: v })}
            />
          </div>
          {order.power_of_attorney === 'Yes' && (
            <span className="rail-note" style={{ marginTop: 6, display: 'block' }}>
              Power of Attorney closings require wet-ink signatures — Hybrid eClose is not available.
            </span>
          )}

          {crossesMonth && (
            <div className="dating-readonly">
              <Icon name="calendar" size={13} />
              <span>
                <b>
                  {effectiveDating === 'next-month'
                    ? `Docs dated for ${monthName(cIdx)}`
                    : `Funding into ${monthName(cIdx)}`}
                </b>
                {' — '}signing in {monthName(sIdx)}, funding in {monthName(cIdx)} · first payment {fpLabel || '—'}
              </span>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ---- Read-only summary (downstream / post-submission) --------------------
  if (!editable) {
    const fpVal = order.first_payment_date || displayDd.firstPayment;
    const fpLabel = fpVal ? fpLabelFrom(fpVal) : '';
    const llcParts: (string | null | undefined)[] = [
      wf.llcEntityName,
      wf.llcEntityState,
      wf.llcEntityOrgType,
      wf.llcEntityEIN ? `EIN ${wf.llcEntityEIN}` : null
    ];
    const llcSummary = llcParts.filter(Boolean).join(' · ');
    return (
      <section className="ws-section" id="sec-closing">
        <div className="card card-pad snapshot-compact">
          <div className="ws-section-head">
            <h3 className="ws-section-title">
              <span className="ws-step-no">{stepNo}</span>Closing Details
            </h3>
            <Chip>
              <Icon name="unlink" size={10} style={{ marginRight: 3, marginBottom: -1 }} />Entered by Processing — not synced to Encompass
            </Chip>
          </div>
          <div className="field-grid compact">
            <SumField
              label="Borrower Vesting"
              value={order.vesting}
              span
              sub={
                order.vesting_confirmed ? (
                  <span className="sum-confirmed">
                    <Icon name="checkCircle" size={11} /> Confirmed by Processing
                  </span>
                ) : null
              }
            />
            <SumField label="Signing Date" value={fmtDate(order.signing_date)} mono />
            <SumField
              label="Funding Date"
              value={fmtDate(order.closing_date)}
              mono
              warn={lockExpired || fundingBeforeSigning}
              warnTip={fundingBeforeSigning ? 'Funding is before the signing date' : 'Closes after the lock expiration'}
              sub={
                fundingBeforeSigning
                  ? `Funding is before the signing date (${fmtDate(order.signing_date)}) — must be the signing date or later.`
                  : lockExpired
                    ? `Closes after lock expires (${fmtDate(loanFacts.lockExpiration)}) — lock extension required.`
                    : null
              }
            />
            <SumField label="First Payment" value={fpLabel} mono />
            <SumField label="Signing Method" value={order.sign_type} />
            {order.power_of_attorney === 'Yes' && (
              <SumField
                label="Power of Attorney"
                value="Yes"
                warn
                warnTip="POA closing"
                sub="Executed POA must be in the eFolder and approved by underwriting before docs release."
              />
            )}
          </div>
          {(wf.closingInTrust || wf.closingInLlc || wf.titleOnlyBorrower) && (
            <div className="sum-flags">
              {wf.closingInTrust && (
                <StatusPill kind="neutral" icon="check">
                  Closing in Trust
                </StatusPill>
              )}
              {wf.closingInLlc && (
                <StatusPill kind="neutral" icon="check">
                  Closing in LLC
                </StatusPill>
              )}
              {wf.titleOnlyBorrower && (
                <StatusPill kind="neutral" icon="user">
                  {wf.closingInTrust ? 'Title Only Trustee' : 'Title Only Borrower'} · {wf.titleOnlyName || '—'}
                </StatusPill>
              )}
            </div>
          )}
          {wf.closingInTrust && <AttorneyTrustReview notRequired={!!wf.attorneyTrustReviewNotRequired} editable={false} />}
          {wf.closingInLlc && llcSummary && (
            <div className="sum-entity">
              <Icon name="building" size={12} />
              <span>{llcSummary}</span>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ---- Processor-editable form ---------------------------------------------
  const fpLabel = displayDd.firstPayment ? fpLabelFrom(displayDd.firstPayment) : '';
  const encVestingDiffers = !!order.encompass_vesting && order.encompass_vesting !== (order.vesting || '');

  return (
    <section className="ws-section" id="sec-closing">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no">{stepNo}</span>Closing Details
          </h3>
          <Chip>
            <Icon name="unlink" size={10} style={{ marginRight: 3, marginBottom: -1 }} />Information in this section is not synced back to Encompass
          </Chip>
        </div>

        <div className="reqdates-row">
          <div className="reqdates-field">
            <span className="reqdates-lbl">
              Docs needed by <span className="req">*</span>
              <span
                className="field-hint"
                tabIndex={0}
                data-tip="Requested signing date — or the date docs are needed at escrow. Independent of the Encompass closing/disclosure dates."
                aria-label="Requested signing date"
              >
                <Icon name="alertCircle" size={13} />
              </span>
            </span>
            <input
              className={`inp ${!order.signing_date ? 'inp-unset' : ''}`.trim()}
              type="date"
              aria-label="Requested signing date"
              value={order.signing_date ?? ''}
              onChange={(e) => patchClosingDates({ signing_date: e.target.value, doc_dating: null })}
            />
          </div>
          <div className="reqdates-field">
            <span className="reqdates-lbl">
              Funding on <span className="req">*</span>
              {(lockExpired || fundingBeforeSigning) && (
                <span className="ro-warn-tag" title={fundingBeforeSigning ? 'Funding cannot be before signing' : 'Closes after the lock expiration'}>
                  <Icon name="alertTriangle" size={11} />
                </span>
              )}
            </span>
            <input
              className={`inp ${lockExpired || fundingBeforeSigning ? 'inp-warn' : !order.closing_date ? 'inp-unset' : ''}`.trim()}
              type="date"
              aria-label="Funding date"
              value={order.closing_date ?? ''}
              min={order.signing_date || undefined}
              onChange={(e) => patchClosingDates({ closing_date: e.target.value, doc_dating: null })}
            />
            {fundingBeforeSigning && (
              <span className="rail-note" style={{ color: 'var(--bad-fg)' }}>
                Funding date can&rsquo;t be before the signing date ({fmtDate(order.signing_date)}) — set it to the signing date or later.
              </span>
            )}
            {lockExpired && (
              <span className="rail-note" style={{ color: 'var(--bad-fg)' }}>
                Closes after lock expires ({fmtDate(loanFacts.lockExpiration)}) — lock extension required.
              </span>
            )}
          </div>
        </div>
        <p className="reqdates-note">
          Requested dates — independent of the Encompass closing date and any disclosure dates. Final dates are confirmed by Closing.
        </p>

        <div className="vesting-summary-block">
          <span className="field-label">
            Borrower Vesting <span className="req">*</span>
            <span className="vsum-enc" title="Field 1867 — Borrower Vesting · synced from Encompass">
              <Icon name="link" size={11} />
            </span>
          </span>
          <div className={`vsum-row ${!order.vesting || !order.vesting_confirmed ? 'unset' : ''}`.trim()}>
            <div className="vsum-val">{order.vesting || <span className="vsum-empty">Not set — open Vesting Details</span>}</div>
            {/* R4 #18 — STATEFUL: attn until the vesting is confirmed, settled
                after (mirrors the fee pills' color language). */}
            <button
              type="button"
              className={`recon-btn ${order.vesting && order.vesting_confirmed ? 'settled' : 'attn'}`}
              title="Reconcile"
              onClick={() => setVestingOpen(true)}
            >
              {order.vesting && order.vesting_confirmed ? 'Reconciled' : 'Reconcile'}
            </button>
          </div>
          {(wf.titleOnlyBorrower || wf.closingInTrust || wf.closingInLlc) && (
            <div className="vsum-flags">
              {wf.closingInTrust && (
                <span className="vsum-pill">
                  <Icon name="check" size={10} strokeWidth={3} />Closing in Trust
                </span>
              )}
              {wf.closingInLlc && (
                <span className="vsum-pill">
                  <Icon name="check" size={10} strokeWidth={3} />Closing in LLC
                </span>
              )}
              {wf.titleOnlyBorrower && (
                <span className="vsum-pill">
                  <Icon name="user" size={10} />
                  {wf.closingInTrust ? 'Title Only Trustee' : 'Title Only Borrower'}
                  {wf.titleOnlyName ? ` · ${wf.titleOnlyName}` : ''}
                </span>
              )}
            </div>
          )}
          {/* Read-only provenance: the current Encompass value, shown only when it
              differs from the working vesting. No edit/confirm/write-back (P3). */}
          {encVestingDiffers && (
            <div className="enc-vest-block">
              <span className="field-label">Current vesting per Encompass</span>
              <div className="enc-vest-row">
                <div className="enc-vest-val">{order.encompass_vesting}</div>
              </div>
              {role === 'closing' ? (
                <div className={`enc-vest-attest ${order.encompass_vesting_confirmed ? 'is-confirmed' : ''}`.trim()}>
                  <span className="enc-vest-note diff">
                    <Icon name="alertTriangle" size={11} />Differs from the vesting submitted by the Processor. Verify for accuracy and confirm once Encompass reflects the correct data.
                  </span>
                  <label className={`enc-vest-confirm ${order.encompass_vesting_confirmed ? 'done' : ''}`}>
                    {!order.encompass_vesting_confirmed && (
                      <span className="req-star" title="Required before sending to the LO">
                        *
                      </span>
                    )}
                    <input
                      type="checkbox"
                      checked={!!order.encompass_vesting_confirmed}
                      disabled={!editable || busy || !!order.encompass_vesting_confirmed}
                      onChange={(e) => {
                        if (e.target.checked) {
                          void vestingAttest();
                        }
                      }}
                    />
                    <span>
                      {order.encompass_vesting_confirmed
                        ? `Confirmed — current vesting per Encompass is accurate.${order.encompass_vesting_confirmed_at ? ` · ${fmtStamp(order.encompass_vesting_confirmed_at)}` : ''}`
                        : 'I confirm the current vesting per Encompass is accurate.'}
                    </span>
                  </label>
                </div>
              ) : (
                <span className="enc-vest-note diff">
                  <Icon name="alertTriangle" size={11} />Differs from the vesting entered here — verify before docs are drawn.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="field-grid compact">
          <div className="field">
            <span className="field-label">
              First Payment Date <span className="req">*</span>
              <span
                className="field-hint"
                tabIndex={0}
                data-tip="Auto-calculated from the funding date and doc-dating rule (doc month + 2 months). Read-only."
                aria-label="Auto-calculated from the funding date and doc-dating rule"
              >
                <Icon name="alertCircle" size={13} />
              </span>
            </span>
            <div className={`inp inp-readonly inp-autofill ${!fpLabel ? 'inp-autofill-empty' : ''}`.trim()} aria-readonly="true">
              <span>{fpLabel || (crossesMonth ? 'Choose doc dating below' : 'Set the funding date')}</span>
            </div>
          </div>
          <div className="field">
            <span className="field-label">
              Power of Attorney? <span className="req">*</span>
            </span>
            <ClosingSeg
              value={order.power_of_attorney}
              editable={editable}
              onPick={(v) => patchClosing({ power_of_attorney: v })}
              options={[
                { value: 'No', label: 'No' },
                {
                  value: 'Yes',
                  label: 'Yes',
                  tip: entityClosing
                    ? `Power of Attorney is not permitted on a${wf.closingInTrust ? ' trust' : 'n LLC'} closing — the ${wf.closingInTrust ? 'trustee' : 'managing member'} must sign.`
                    : undefined
                }
              ]}
              disabledOptions={entityClosing ? ['Yes'] : []}
            />
            {entityClosing && (
              <span className="rail-note" style={{ marginTop: 4 }}>
                Not permitted on a{wf.closingInTrust ? ' trust' : 'n LLC'} closing — {wf.closingInTrust ? 'the trustee must sign in person' : 'all members must sign in person'}.
              </span>
            )}
            {order.power_of_attorney === 'Yes' && (
              <div className="banner info compact-banner" style={{ marginTop: 8 }}>
                <Icon name="alertCircle" size={14} className="banner-ico" />
                <span>
                  <b>Power of Attorney.</b> Docs can be released once the executed POA is uploaded to the eFolder and approved by underwriting.
                </span>
              </div>
            )}
          </div>
          <div className="field span-2">
            <span className="field-label">
              Signing Method <span className="req">*</span>
            </span>
            <ClosingSeg
              value={order.sign_type}
              editable={editable}
              onPick={(v) => patchClosing({ sign_type: v })}
              options={[
                {
                  value: 'Hybrid eClose',
                  label: 'Hybrid eClose',
                  tip: entityClosing
                    ? `Hybrid eClose is not supported for ${wf.closingInTrust ? 'trust' : 'LLC'} closings — wet ink is required.`
                    : poaYes
                      ? 'Power of Attorney requires Wet Sign — Hybrid eClose is not permitted.'
                      : undefined
                },
                { value: 'Wet Sign', label: 'Wet Sign' }
              ]}
              disabledOptions={entityClosing || poaYes ? ['Hybrid eClose'] : []}
            />
            {entityClosing ? (
              <span className="rail-note" style={{ marginTop: 4 }}>
                {wf.closingInTrust ? 'Trust' : 'LLC'} closings require wet-ink signatures — Hybrid eClose is not available.
              </span>
            ) : poaYes ? (
              <span className="rail-note" style={{ marginTop: 4 }}>
                Power of Attorney closings require wet-ink signatures — Hybrid eClose is not available.
              </span>
            ) : null}
          </div>
        </div>

        {crossesMonth && (
          <div className={`banner warn dating-banner ${effectiveDating ? 'dating-selected' : ''}`.trim()} style={{ marginTop: 16, display: 'block' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Icon name="alertTriangle" size={16} className="banner-ico" />
              <div>
                <b>Signing and closing fall in different months.</b> Signing is in {monthName(sIdx)} but funding is in {monthName(cIdx)}. How should the docs be
                dated? This sets the first payment date.
              </div>
            </div>
            <div className="dating-choice">
              <button
                type="button"
                className={`dating-opt ${effectiveDating === 'fund-into' ? 'active' : ''} ${fundDeepIntoMonth ? 'dating-opt-disabled' : ''}`.trim()}
                disabled={fundDeepIntoMonth}
                title={fundDeepIntoMonth ? 'Funding date is more than 5 days into the following month' : undefined}
                onClick={() => {
                  if (!fundDeepIntoMonth) {
patchClosingDates({ doc_dating: 'fund-into' });
}
                }}
              >
                <span className="do-radio" />
                <span>
                  <span className="do-title">Fund into {monthName(cIdx, { month: 'long' })}</span>
                  <span className="do-sub">
                    {monthName(sIdx, { month: 'long' })} docs, funding ≤5 days into {monthName(cIdx, { month: 'long' })} → first payment{' '}
                    <b>{monthName((sIdx === null || sIdx === undefined) ? null : sIdx + 2)}</b>
                  </span>
                  {fundDeepIntoMonth && (
                    <span className="do-flag">
                      <Icon name="alertTriangle" size={11} />Funding is more than 5 days into {monthName(cIdx, { month: 'long' })}
                    </span>
                  )}
                </span>
              </button>
              <button
                type="button"
                className={`dating-opt ${effectiveDating === 'next-month' ? 'active' : ''}`.trim()}
                onClick={() => patchClosingDates({ doc_dating: 'next-month' })}
              >
                <span className="do-radio" />
                <span>
                  <span className="do-title">Date docs for {monthName(cIdx, { month: 'long' })}</span>
                  <span className="do-sub">
                    Docs re-dated to {monthName(cIdx, { month: 'long' })} → first payment <b>{monthName((cIdx === null || cIdx === undefined) ? null : cIdx + 2)}</b>
                  </span>
                  <span className="do-flag">
                    <Icon name="alertTriangle" size={11} />Cannot fund before {monthName(cIdx, { month: 'short' })} 1
                  </span>
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      {vestingOpen && <VestingSlideOver order={order} onClose={() => setVestingOpen(false)} />}
    </section>
  );
}
